import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api.service';
import { PUBLIC_SITE_URL } from '../config/api';
import { Button } from './ui/button';
import { Mail, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';

export default function EmailVerificationScreen() {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleResend = async () => {
    setSending(true);
    setError('');
    setSent(false);
    try {
      await apiService.resendVerificationEmail();
      setSent(true);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError(t('emailVerification.rateLimited'));
      } else {
        setError(t('emailVerification.sendError'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await checkAuth();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <img
          src={`${PUBLIC_SITE_URL}/logos/LOGFIT-LOGO-ORANGE-PATH.svg`}
          alt="LogFit"
          className="h-10 mx-auto mb-8"
        />

        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-white/[0.06] flex items-center justify-center mb-6">
          <Mail className="w-8 h-8 text-foreground" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-3">
          {t('emailVerification.title')}
        </h1>

        {/* Description */}
        <p className="text-white/60 mb-2">
          {t('emailVerification.description')}
        </p>
        <p className="text-white/80 font-medium mb-8">
          {user?.email}
        </p>

        {/* Success message */}
        {sent && (
          <div className="flex items-center justify-center gap-2 text-success mb-4">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm">{t('emailVerification.sent')}</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <p className="text-destructive text-sm mb-4">{error}</p>
        )}

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleResend}
            disabled={sending}
            className="w-full"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            {t('emailVerification.resend')}
          </Button>

          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/10"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {t('emailVerification.refresh')}
          </Button>
        </div>

        {/* Help text */}
        <p className="text-white/40 text-xs mt-6">
          {t('emailVerification.checkSpam')}
        </p>
        <p className="text-white/40 text-xs mt-2">
          <a
            href={`${PUBLIC_SITE_URL}/contact`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-white/60 transition-colors"
          >
            {t('emailVerification.wrongEmail')}
          </a>
        </p>
      </div>
    </div>
  );
}
